import type { PhotoCredit } from "../shared/liveList";
import type { PoiGroup } from "../shared/mapModel";
import { asset } from "./assets";
import data from "./madMonkeyData.json";

/**
 * Content of the Stippl x Mad Monkey partner page (/mad-monkey), as Ilse's approved prototype had it:
 * 23 hostels, their nearby places and the five ready-made routes. Static on purpose, with one exception: a hostel's own
 * page takes its nearby places from the hostel's synced Stippl list once that exists (`livePois.ts`), these as fallback.
 *
 * `madMonkeyData.json` is the prototype's `data.json` minus two things: the static Mapbox images (the maps are
 * live now) and the hostels' prices (the page shows none). Its photo paths are the prototype's relative ones;
 * `asset()` (assets.ts) turns them into R2 URLs.
 */

export type HostelKind = "city" | "town" | "island";

export interface Poi {
	name: string;
	/** the place's Google place id, from its knowledge-base place (explore/places, matched on name and coordinates) */
	gid: string;
	grp: PoiGroup;
	icon: string;
	cat: string;
	lat: number;
	lon: number;
	why: string;
	label: string;
	/** km from the hostel */
	dist: number;
	/** the static places' Google rating; a place from the hostel's synced list has none */
	rating?: number;
	photo?: string;
	/** what the photo's licence asks to show, behind the map's (i): only a photo from a synced list carries one (`livePois.ts`); the static photos are Unsplash's, which asks for none */
	credit?: PhotoCredit;
}

export interface Hostel {
	name: string;
	/** ISO 3166-1 alpha-2, lowercase; the key of `COUNTRIES` */
	cc: string;
	lat: number;
	lon: number;
	tag: string;
	events: string[];
	todo: string[];
	amen: string[];
	about: string;
	/** the hostel's page on madmonkeyhostels.com */
	url: string;
	/** the hostel's id in the Nuitee catalogue, e.g. "lp71686" */
	nuitee: string;
	kind: HostelKind;
	photos: string[];
	/** two hostels (Koh Sdach, Dumaguete) have none yet. A hostel's synced list replaces these on its page (`livePois.ts`) */
	pois?: Poi[];
}

export interface Trip {
	id: string;
	/** the route as a synced Stippl trip (stippl-knowledge `explore/partner-recipes/mad-monkey.md`, its `Trip:` line): what the route cards open */
	tripId: string;
	name: string;
	sub: string;
	blurb: string;
	/** [hostel slug, nights] in route order */
	stops: [string, number][];
	/** how to get from stop i to stop i + 1 */
	legs: string[];
	tag: string;
	vibe: string;
	vibes: string[];
}

interface RawContent {
	countries: Record<string, string>;
	hostels: Record<string, Hostel>;
	trips: Trip[];
	/** the hostels with a map page of their own, `/p/mad-monkey-<slug>`: those with a synced list. `MAD_MONKEY_HOSTEL_LISTS` in graphql/helpers/partnerMapLoader.ts is checked against it */
	mapHostels: string[];
}

const raw = data as unknown as RawContent;

/** country code to full English name, e.g. th: "Thailand" */
export const COUNTRIES: Record<string, string> = raw.countries;

/** hostel slug to hostel, in the prototype's order */
export const HOSTELS: Record<string, Hostel> = Object.fromEntries(
	Object.entries(raw.hostels).map(([slug, h]) => [
		slug,
		{ ...h, photos: h.photos.map(asset), pois: h.pois?.map((p) => ({ ...p, photo: p.photo && asset(p.photo) })) },
	])
);

export const HOSTEL_SLUGS: string[] = Object.keys(HOSTELS);

/** a hostel's photos as the media paths the file holds, for a page that resolves its own media folder (the partner map registry) */
export const hostelPhotoPaths = (slug: string): string[] => raw.hostels[slug].photos;

export const TRIPS: Trip[] = raw.trips;

export const MAP_HOSTELS: string[] = raw.mapHostels;

export const getTrip = (id: string): Trip | undefined => TRIPS.find((t) => t.id === id);

export const getHostel = (slug: string): Hostel | undefined => (Object.prototype.hasOwnProperty.call(HOSTELS, slug) ? HOSTELS[slug] : undefined);

/** the banana pancake route: what the planner starts with, and the home page's flagship */
export const FLAGSHIP_ID = "banana-pancake";

export const nightsOf = (trip: Trip): number => trip.stops.reduce((total, [, n]) => total + n, 0);

export const countriesOf = (slugs: string[]): string[] => [...new Set(slugs.map((s) => HOSTELS[s].cc))];

export const tripsVia = (slug: string): Trip[] => TRIPS.filter((t) => t.stops.some(([s]) => s === slug));
